/**
 * Seeded, serialisable pseudo-random numbers for the playtest engine.
 *
 * mulberry32 over a single uint32 of state. The API is pure: drawing returns
 * `[value, nextState]` and never alters the state it was given, so a state can
 * live inside engine state, be persisted as a plain number, and replay a whole
 * session from one seed. Integer-only arithmetic (`Math.imul`, `>>>`) plus one
 * final division makes results bit-identical on V8 (web, Node) and Hermes
 * (mobile).
 *
 * Seed GENERATION (clock, `Math.random`) deliberately lives outside this module:
 * the engine is pure and the UI layer hands a seed in. This file must not read
 * ambient randomness or time (enforced by lint, see mobile/.eslintrc.shared.js).
 */

/** Generator state: one unsigned 32-bit integer. A plain number, so it serialises as-is. */
export type RngState = number;

/** Result of one draw: the value in [0, 1) and the state to draw the next from. */
export type RngDraw = readonly [value: number, next: RngState];

/** mulberry32's state increment (the golden-ratio-derived odd constant of the algorithm). */
const STATE_INCREMENT = 0x6d2b79f5;

/** Output-mixing constant of mulberry32's second multiply-xor round. */
const MIX_MULTIPLIER = 61;

/** Shift amounts of mulberry32's three xor-shift rounds. */
const SHIFT_FIRST = 15;
const SHIFT_SECOND = 7;
const SHIFT_FINAL = 14;

/** 2^32: the divisor that maps a uint32 onto [0, 1). */
const UINT32_RANGE = 0x100000000;

/**
 * Normalises any numeric seed to a uint32 state. Fractions are truncated and
 * negatives wrap modulo 2^32 (`-1` becomes `0xFFFFFFFF`); NaN and the
 * infinities become `0`.
 */
export function createRngState(seed: number): RngState {
  return Math.trunc(seed) >>> 0;
}

/** Draws a float in [0, 1) and returns it with the next state. */
export function nextRandom(state: RngState): RngDraw {
  const next = (state + STATE_INCREMENT) >>> 0;
  let t = Math.imul(next ^ (next >>> SHIFT_FIRST), 1 | next);
  t = (t + Math.imul(t ^ (t >>> SHIFT_SECOND), MIX_MULTIPLIER | t)) ^ t;
  const value = ((t ^ (t >>> SHIFT_FINAL)) >>> 0) / UINT32_RANGE;
  return [value, next];
}

/**
 * Draws an integer in [0, maxExclusive).
 *
 * `floor(value * max)` carries a bias of order max / 2^32, negligible for the
 * hundreds-of-cards range this engine works in, so no rejection sampling.
 *
 * @throws RangeError when `maxExclusive` is not a positive integer
 */
export function randomInt(
  state: RngState,
  maxExclusive: number,
): readonly [value: number, next: RngState] {
  if (!Number.isInteger(maxExclusive) || maxExclusive < 1) {
    throw new RangeError(
      `randomInt: maxExclusive must be a positive integer, got ${maxExclusive}`,
    );
  }
  const [value, next] = nextRandom(state);
  return [Math.floor(value * maxExclusive), next];
}
